export type Role = 'RESIDENT' | 'HEALTHCARE_PROVIDER' | 'INSTALLER' | 'WATCHERR_EMPLOYEE' | 'SERVICE';

export const ROLE_RANK: Record<Role, number> = {
  RESIDENT: 0,
  HEALTHCARE_PROVIDER: 1,
  INSTALLER: 2,
  WATCHERR_EMPLOYEE: 3,
  SERVICE: 4,
};

export type FieldType =
  | 'TextField'
  | 'PasswordField'
  | 'NumberField'
  | 'ToggleSwitch'
  | 'DropDown'
  | 'TextArea'
  | 'CodeBlock'
  | 'StatusBlock'
  | 'Polygon';

export type Constraint =
  | { notNull: { message: string } }
  | { range: { min: number; max: number; message: string } }
  | { regex: { regex: string; message: string } }
  | {
      comparator: {
        type: 'greaterThan' | 'greaterThanOrEqual' | 'lessThan' | 'lessThanOrEqual' | 'equalTo';
        field: string;
        message: string;
      };
    }
  | {
      validation_function: {
        name: string;
        params?: string[];
        message: string;
      };
    };

export type DropDownOption = {
  value: string | number | boolean;
  label_key?: string;
  props?: FieldValues;
};

export interface FieldAction {
  label_key: string;
  variant?: 'primary' | 'ghost' | 'danger';
}

export interface Field {
  id: string;
  type: FieldType;
  title: string;
  tooltip?: string;
  unit?: string;
  default?: string | number | boolean;
  site_config_value?: FieldValue;
  placeholder?: string;
  span?: 1 | 2 | 3 | 4;
  sub_section?: string;
  enabled?: boolean;
  required_role?: Role;
  required_permissions?: string[];
  supported_since?: string;
  deprecated_since?: string;
  highlight?: boolean;
  options?: DropDownOption[];
  visible_if?: string;
  enabled_if?: string;
  device_model_level?: boolean;
  advanced?: boolean;
  constraints?: Constraint[];
  format?: string;
  action?: FieldAction;
}

export interface SubSection {
  id: string;
  title_key: string;
}

export type SubProfileOption = DropDownOption & { label_key: string; value: string };

export interface SubProfile {
  label_key: string;
  selected?: string;
  options: SubProfileOption[];
}

export interface Section {
  title_key: string;
  icon?: string;
  icon_color?: string;
  cols?: 1 | 2 | 3 | 4;
  collapsed?: boolean;
  advanced_toggle?: boolean;
  sub_profile?: SubProfile;
  sub_sections?: SubSection[];
  fields: Field[];
}

export type LayoutRow = { section: string } | { split: [string, string] };

export interface SchemaAction {
  id: string;
  label_key: string;
  variant?: 'primary' | 'ghost' | 'danger';
  icon?: string;
  placement?: 'header' | 'actionbar';
  required_role?: Role;
  required_permissions?: string[];
  split?: { id: string; label_key: string }[];
  confirm?: { title_key: string; body_key: string };
}

export interface SchemaMeta {
  title_key: string;
  subtitle_key: string;
  source_file?: string;
  device_mac?: string;
  firmware?: string;
  profile?: { selected: string; options: string[] };
}

export interface Schema {
  meta: SchemaMeta;
  layout: LayoutRow[];
  sections: Record<string, Section>;
  actions: SchemaAction[];
}

export type FieldValue = string | number | boolean | null | undefined;
export type FieldValues = Record<string, FieldValue>;

export const collectAllFields = (schema: Schema): Field[] => {
  const out: Field[] = [];
  for (const section of Object.values(schema.sections)) {
    for (const f of section.fields) out.push(f);
  }
  return out;
};
